import { useState, useEffect, useMemo, useCallback } from 'react';
import { Play, Copy, Check } from 'lucide-react';
import RichTextRenderer from '../RichTextRenderer';
import { adminApi } from '../../utils/api';
import {
    Button, Badge, Modal, Alert, SearchBar, Tabs, Spinner,
    InfoRow, Input, Textarea, Toggle, Select,
} from './shared';

const VIEW_TABS = [
    { id: 'list', label: 'All Tools' },
    { id: 'toolkits', label: 'Toolkits' },
];

function buildMcpNamespaceMap(servers) {
    const map = new Map();
    (servers || []).forEach(s => {
        const ns = s.namespace || s.id?.replace(/-/g, '_');
        if (ns) map.set(ns, s);
    });
    return map;
}

function matchToolToServer(toolName, nsMap) {
    for (const [ns, server] of nsMap) {
        if (toolName.startsWith(ns + '__')) return server;
    }
    return null;
}

export default function AdminTools() {
    const [tools, setTools] = useState([]);
    const [toolkits, setToolkits] = useState([]);
    const [mcpServers, setMcpServers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTool, setSelectedTool] = useState(null);
    const [selectedToolkit, setSelectedToolkit] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('list');
    const [sourceFilter, setSourceFilter] = useState('all');

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [toolsRes, toolkitsRes, mcpRes] = await Promise.all([
                adminApi.listTools(),
                adminApi.listToolkits(),
                adminApi.listMCPServers().catch(() => ({ servers: [] })),
            ]);
            setTools(toolsRes.tools || []);
            setToolkits(toolkitsRes.toolkits || []);
            setMcpServers(mcpRes.servers || []);
        } catch (err) {
            setError(err.message || 'Failed to load tools');
        } finally {
            setLoading(false);
        }
    };

    const nsMap = useMemo(() => buildMcpNamespaceMap(mcpServers), [mcpServers]);

    const sourceOptions = useMemo(() => {
        const serverCounts = new Map();
        let localCount = 0;
        tools.forEach(tool => {
            const server = matchToolToServer(tool.name, nsMap);
            if (server) {
                serverCounts.set(server.id, (serverCounts.get(server.id) || 0) + 1);
            } else {
                localCount++;
            }
        });
        const options = [{ id: 'all', label: 'All', count: tools.length }];
        mcpServers
            .filter(s => serverCounts.has(s.id))
            .sort((a, b) => (serverCounts.get(b.id) || 0) - (serverCounts.get(a.id) || 0))
            .forEach(s => {
                options.push({ id: s.id, label: s.name, count: serverCounts.get(s.id) });
            });
        if (localCount > 0) {
            options.push({ id: 'local', label: 'Local', count: localCount });
        }
        return options;
    }, [tools, mcpServers, nsMap]);

    const filteredTools = useMemo(() => {
        let result = tools;
        if (sourceFilter !== 'all') {
            if (sourceFilter === 'local') {
                result = result.filter(t => !matchToolToServer(t.name, nsMap));
            } else {
                const server = mcpServers.find(s => s.id === sourceFilter);
                if (server) {
                    const ns = server.namespace || server.id?.replace(/-/g, '_');
                    result = result.filter(t => t.name.startsWith(ns + '__'));
                }
            }
        }
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(t =>
                t.name.toLowerCase().includes(q) ||
                (t.description && t.description.toLowerCase().includes(q)) ||
                (t.tags && t.tags.some(tag => tag.toLowerCase().includes(q)))
            );
        }
        return result;
    }, [tools, sourceFilter, searchQuery, nsMap, mcpServers]);

    const totalTokens = tools.reduce((sum, t) => sum + (t.token_count || 0), 0);
    const filteredTokens = filteredTools.reduce((sum, t) => sum + (t.token_count || 0), 0);

    const handleViewTool = async (toolName) => {
        try {
            const tool = await adminApi.getTool(toolName);
            setSelectedTool(tool);
            setSelectedToolkit(null);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleViewToolkit = async (toolkitName) => {
        try {
            const res = await adminApi.getToolkitTools(toolkitName);
            setSelectedToolkit({ name: toolkitName, tools: res.tools || [] });
            setSelectedTool(null);
        } catch (err) {
            setError(err.message);
        }
    };

    const isFiltered = sourceFilter !== 'all' || searchQuery;

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Tools</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {isFiltered
                            ? <>{filteredTools.length} of {tools.length} tools — ~{filteredTokens.toLocaleString()} tokens</>
                            : <>{tools.length} tool{tools.length !== 1 ? 's' : ''} — ~{totalTokens.toLocaleString()} total tokens</>
                        }
                    </p>
                </div>
                <Tabs tabs={VIEW_TABS} active={viewMode} onChange={setViewMode} />
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            {viewMode === 'list' && (
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <SearchBar
                        value={searchQuery}
                        onChange={setSearchQuery}
                        onSearch={loadData}
                        onRefresh={loadData}
                        placeholder="Search tools..."
                        className="flex-1"
                    />
                    <select
                        value={sourceFilter}
                        onChange={(e) => setSourceFilter(e.target.value)}
                        className="h-9 px-3 pr-8 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat"
                    >
                        {sourceOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>
                                {opt.label} ({opt.count})
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {viewMode === 'toolkits' && (
                <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onSearch={loadData}
                    onRefresh={loadData}
                    placeholder="Search toolkits..."
                    className="mb-4"
                />
            )}

            {loading ? (
                <Spinner size="lg" text="Loading tools..." center />
            ) : viewMode === 'list' ? (
                <ToolsTable tools={filteredTools} nsMap={nsMap} onView={handleViewTool} />
            ) : (
                <ToolkitsView
                    toolkits={toolkits}
                    searchQuery={searchQuery}
                    onViewToolkit={handleViewToolkit}
                />
            )}

            {selectedTool && (
                <ToolViewModal tool={selectedTool} onClose={() => setSelectedTool(null)} />
            )}

            {selectedToolkit && (
                <ToolkitViewModal
                    toolkit={selectedToolkit}
                    onViewTool={(name) => { setSelectedToolkit(null); handleViewTool(name); }}
                    onClose={() => setSelectedToolkit(null)}
                />
            )}
        </div>
    );
}

function ToolsTable({ tools, nsMap, onView }) {
    if (tools.length === 0) {
        return <div className="text-center py-12 text-gray-500 text-sm">No tools found</div>;
    }

    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/60">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Tool</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 w-16">Tokens</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-32">Source</th>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Tags</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {tools.map((tool) => {
                        const server = matchToolToServer(tool.name, nsMap);
                        const ns = server ? (server.namespace || server.id?.replace(/-/g, '_')) : null;
                        const shortName = ns ? tool.name.substring(ns.length + 2) : tool.name;
                        return (
                            <tr
                                key={tool.name}
                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                                onClick={() => onView(tool.name)}
                            >
                                <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-1.5">
                                        {ns && <span className="text-[10px] text-gray-400 font-mono">{ns}__</span>}
                                        <span className="text-xs font-medium font-mono text-gray-900">{shortName}</span>
                                    </div>
                                    {tool.description && (
                                        <p className="text-xs text-gray-500 truncate max-w-lg mt-0.5">{tool.description}</p>
                                    )}
                                </td>
                                <td className="px-3 py-2.5 text-right">
                                    <span className="text-xs text-gray-500 font-mono">{tool.token_count || '-'}</span>
                                </td>
                                <td className="px-3 py-2.5">
                                    {server
                                        ? <Badge variant="purple">{server.name}</Badge>
                                        : <Badge variant="default">Local</Badge>
                                    }
                                </td>
                                <td className="px-3 py-2.5">
                                    <div className="flex items-center gap-1 flex-wrap">
                                        {(tool.tags || []).slice(0, 3).map(tag => (
                                            <Badge key={tag} variant="outline" className="text-[10px]">{tag}</Badge>
                                        ))}
                                        {(tool.tags || []).length > 3 && (
                                            <span className="text-[10px] text-gray-400">+{tool.tags.length - 3}</span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function ToolkitsView({ toolkits, searchQuery, onViewToolkit }) {
    const filtered = toolkits.filter(tk => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return tk.name.toLowerCase().includes(q) || tk.tools.some(t => t.toLowerCase().includes(q));
    });

    if (filtered.length === 0) {
        return <div className="text-center py-12 text-gray-500 text-sm">No toolkits found</div>;
    }

    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/60">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Toolkit</th>
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Tools</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Members</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {filtered.map((tk) => (
                        <tr
                            key={tk.name}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => onViewToolkit(tk.name)}
                        >
                            <td className="px-4 py-2.5">
                                <span className="text-sm font-medium text-gray-900">{tk.name}</span>
                            </td>
                            <td className="px-3 py-2.5 text-right">
                                <Badge variant="info">{tk.tool_count}</Badge>
                            </td>
                            <td className="px-4 py-2.5">
                                <p className="text-xs text-gray-500 truncate max-w-md">
                                    {tk.tools.slice(0, 4).join(', ')}
                                    {tk.tools.length > 4 && ` +${tk.tools.length - 4} more`}
                                </p>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const MODAL_TABS = [
    { id: 'info', label: 'Info' },
    { id: 'run', label: 'Run Tool' },
];

const RESULT_TABS = [
    { id: 'raw', label: 'Raw JSON' },
    { id: 'markdown', label: 'Markdown' },
];

function ParamField({ param, value, onChange }) {
    const label = (
        <span className="flex items-center gap-2">
            <span className="font-mono">{param.name}</span>
            <Badge variant="outline" className="text-[10px]">{param.type}</Badge>
            {param.required && <span className="text-[10px] text-red-500 font-medium">required</span>}
        </span>
    );

    const hint = param.default !== null && param.default !== undefined
        ? `default: ${JSON.stringify(param.default)}`
        : undefined;

    if (param.type === 'boolean') {
        return (
            <div className="space-y-1">
                <Toggle
                    label={label}
                    checked={value === 'true' || value === true}
                    onChange={(val) => onChange(param.name, String(val))}
                />
                {param.description && (
                    <p className="text-[11px] text-gray-400 pl-0.5">{param.description}</p>
                )}
            </div>
        );
    }

    if (param.enum && param.type !== 'array') {
        return (
            <div className="space-y-1">
                <Select
                    label={label}
                    hint={hint}
                    value={value || ''}
                    onChange={(e) => onChange(param.name, e.target.value)}
                >
                    <option value="">-- Select --</option>
                    {param.enum.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </Select>
                {param.description && (
                    <p className="text-[11px] text-gray-400 pl-0.5">{param.description}</p>
                )}
            </div>
        );
    }

    if (param.type === 'array' || param.type === 'object') {
        return (
            <div className="space-y-1">
                <Textarea
                    label={label}
                    hint={hint}
                    monospace
                    rows={3}
                    value={value || ''}
                    onChange={(e) => onChange(param.name, e.target.value)}
                    placeholder={param.type === 'array' ? '["item1", "item2"]' : '{"key": "value"}'}
                />
                {param.description && (
                    <p className="text-[11px] text-gray-400 pl-0.5">{param.description}</p>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-1">
            <Input
                label={label}
                hint={hint}
                type={param.type === 'integer' || param.type === 'number' ? 'number' : 'text'}
                step={param.type === 'number' ? 'any' : undefined}
                value={value || ''}
                onChange={(e) => onChange(param.name, e.target.value)}
                placeholder={param.description || param.type}
            />
            {param.description && (
                <p className="text-[11px] text-gray-400 pl-0.5">{param.description}</p>
            )}
        </div>
    );
}

function ToolViewModal({ tool, onClose }) {
    const [activeTab, setActiveTab] = useState('info');
    const [paramValues, setParamValues] = useState({});
    const [invoking, setInvoking] = useState(false);
    const [invokeResult, setInvokeResult] = useState(null);
    const [invokeError, setInvokeError] = useState(null);
    const [resultView, setResultView] = useState('raw');
    const [copied, setCopied] = useState(false);

    const allParams = useMemo(() => {
        if (tool.parameters?.length) return tool.parameters;
        if (!tool.input_schema) return [];
        const schema = tool.input_schema;

        const META_KEYS = new Set(['type', 'title', 'required', 'description', '$defs', 'definitions', '$schema']);
        let propEntries = [];
        let requiredSet = new Set();

        if (schema.properties) {
            propEntries = Object.entries(schema.properties);
            requiredSet = new Set(schema.required || []);
        } else {
            propEntries = Object.entries(schema).filter(
                ([k, v]) => !META_KEYS.has(k) && v && typeof v === 'object' && 'type' in v
            );
        }

        return propEntries.map(([name, spec]) => ({
            name,
            type: spec.type || 'string',
            description: spec.description || spec.title || null,
            required: requiredSet.has(name),
            default: spec.default ?? null,
            enum: spec.enum || spec.items?.enum || null,
        }));
    }, [tool]);

    const handleParamChange = useCallback((name, value) => {
        setParamValues(prev => ({ ...prev, [name]: value }));
    }, []);

    const parseArrayValue = useCallback((val) => {
        const trimmed = val.trim();
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) return parsed;
        } catch {}
        try {
            const fixed = trimmed.replace(/'/g, '"');
            const parsed = JSON.parse(fixed);
            if (Array.isArray(parsed)) return parsed;
        } catch {}
        if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) {
            return trimmed.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
        }
        return val;
    }, []);

    const buildParams = useCallback(() => {
        const params = {};
        for (const [key, val] of Object.entries(paramValues)) {
            if (val === '' || val === undefined) continue;
            const paramDef = allParams.find(p => p.name === key);
            if (paramDef?.type === 'integer') {
                params[key] = parseInt(val, 10);
            } else if (paramDef?.type === 'number') {
                params[key] = parseFloat(val);
            } else if (paramDef?.type === 'boolean') {
                params[key] = val === 'true';
            } else if (paramDef?.type === 'array') {
                params[key] = parseArrayValue(val);
            } else if (paramDef?.type === 'object') {
                try { params[key] = JSON.parse(val); } catch {
                    try { params[key] = JSON.parse(val.replace(/'/g, '"')); } catch { params[key] = val; }
                }
            } else {
                params[key] = val;
            }
        }
        return params;
    }, [allParams, paramValues, parseArrayValue]);

    const handleInvoke = useCallback(async () => {
        setInvoking(true);
        setInvokeResult(null);
        setInvokeError(null);
        try {
            const res = await adminApi.invokeTool(tool.name, buildParams());
            if (res.success) {
                setInvokeResult(res);
            } else {
                setInvokeError(res.error || 'Unknown error');
                setInvokeResult(res);
            }
        } catch (err) {
            setInvokeError(err.message || 'Failed to invoke tool');
        } finally {
            setInvoking(false);
        }
    }, [tool, buildParams]);

    const handleCopy = useCallback(() => {
        if (!invokeResult) return;
        navigator.clipboard.writeText(JSON.stringify(invokeResult.result, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [invokeResult]);

    const resultMarkdown = invokeResult?.result_markdown || '';

    return (
        <Modal open onClose={onClose} title={tool.name} size="xl">
            <Modal.Body>
                <Tabs tabs={MODAL_TABS} active={activeTab} onChange={setActiveTab} className="mb-4" />

                {activeTab === 'info' && (
                    <>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                            <InfoRow label="Name" value={tool.name} mono />
                            <InfoRow label="MCP" value={tool.is_mcp ? 'Yes' : 'No'} />
                            {tool.token_count > 0 && <InfoRow label="Tokens" value={`~${tool.token_count}`} />}
                            <InfoRow label="Built-in" value={tool.is_builtin ? 'Yes' : 'No'} />
                        </div>

                        <div className="mt-4">
                            <p className="text-sm font-medium text-gray-700 mb-1">Description</p>
                            <p className="text-sm text-gray-600 whitespace-pre-wrap">{tool.description || 'No description'}</p>
                        </div>

                        {tool.tags && tool.tags.length > 0 && (
                            <div className="mt-4">
                                <p className="text-sm font-medium text-gray-700 mb-2">Tags</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {tool.tags.map(tag => <Badge key={tag} variant="outline">{tag}</Badge>)}
                                </div>
                            </div>
                        )}

                        {allParams.length > 0 && (
                            <div className="mt-4">
                                <p className="text-sm font-medium text-gray-700 mb-2">Parameters ({allParams.length})</p>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {allParams.map(param => (
                                        <div key={param.name} className="p-2.5 bg-gray-50 rounded-md">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-mono text-xs text-blue-700">{param.name}</span>
                                                <Badge variant="outline" className="text-[10px]">{param.type}</Badge>
                                                {param.required && <span className="text-[10px] text-red-500 font-medium">required</span>}
                                                {param.default !== null && param.default !== undefined && (
                                                    <span className="text-[10px] text-gray-400">default: {JSON.stringify(param.default)}</span>
                                                )}
                                            </div>
                                            {param.description && (
                                                <p className="text-[11px] text-gray-500 mt-1">{param.description}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {tool.input_schema && (
                            <div className="mt-4">
                                <p className="text-sm font-medium text-gray-700 mb-2">Input Schema</p>
                                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap max-h-64 overflow-y-auto">
                                    {JSON.stringify(tool.input_schema, null, 2)}
                                </pre>
                            </div>
                        )}

                        {!allParams.length && !tool.input_schema && (
                            <p className="mt-4 text-sm text-gray-400 italic">No parameters or schema available</p>
                        )}
                    </>
                )}

                {activeTab === 'run' && (
                    <>
                        {allParams.length > 0 ? (
                            <div className="space-y-4 mb-4">
                                {allParams.map(param => (
                                    <ParamField
                                        key={param.name}
                                        param={param}
                                        value={paramValues[param.name]}
                                        onChange={handleParamChange}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-400 italic mb-4">This tool has no parameters</p>
                        )}

                        <div className="pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                                <button
                                    onClick={handleInvoke}
                                    disabled={invoking}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {invoking ? (
                                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <Play className="w-3.5 h-3.5" />
                                    )}
                                    {invoking ? 'Running...' : 'Run'}
                                </button>
                                {invokeResult && (
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[10px] font-medium ${invokeResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                            {invokeResult.success ? 'Success' : 'Failed'} · {invokeResult.duration_ms}ms
                                        </span>
                                        <Tabs tabs={RESULT_TABS} active={resultView} onChange={setResultView} />
                                    </div>
                                )}
                            </div>

                            {invokeError && !invokeResult?.success && (
                                <Alert variant="error" className="mb-3">{invokeError}</Alert>
                            )}

                            {invoking && !invokeResult && (
                                <div className="flex items-center justify-center py-8">
                                    <Spinner size="md" text="Invoking tool..." />
                                </div>
                            )}

                            {invokeResult && (
                                <div className="relative">
                                    <button
                                        onClick={handleCopy}
                                        className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-gray-600 bg-white/80 rounded-md border border-gray-200 transition-colors z-10"
                                        title="Copy result"
                                    >
                                        {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                    {resultView === 'raw' ? (
                                        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap max-h-96 overflow-y-auto">
                                            {JSON.stringify(invokeResult.result, null, 2)}
                                        </pre>
                                    ) : (
                                        <div className="bg-white border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                                            <RichTextRenderer text={resultMarkdown} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}

function ToolkitViewModal({ toolkit, onViewTool, onClose }) {
    const totalTokens = toolkit.tools.reduce((s, t) => s + (t.token_count || 0), 0);

    return (
        <Modal open onClose={onClose} title={`Toolkit: ${toolkit.name}`} size="lg">
            <Modal.Body>
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span>{toolkit.tools.length} tools</span>
                    <span>~{totalTokens.toLocaleString()} tokens</span>
                </div>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {toolkit.tools.map(tool => (
                        <div
                            key={tool.name}
                            onClick={() => onViewTool(tool.name)}
                            className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                            <div className="min-w-0">
                                <span className="text-sm font-medium text-gray-900">{tool.name}</span>
                                <p className="text-xs text-gray-500 truncate">{tool.description}</p>
                            </div>
                            {tool.token_count > 0 && (
                                <span className="text-[10px] text-gray-400 shrink-0 ml-2">{tool.token_count} tok</span>
                            )}
                        </div>
                    ))}
                </div>
            </Modal.Body>
        </Modal>
    );
}
